import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Helper function to get API URL based on server type
const getApiUrl = () => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL ;
  const serverType = process.env.REACT_APP_SERVER_TYPE ;

  const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';
  return `${baseUrl}/${webhookPath}/agent_gg_aukik_login`;
};

interface User {
  id: string;
  name: string;
  email: string;
  completedOnboarding: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  completeOnboarding: () => void;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for saved user in sessionStorage and token in localStorage
    const savedUser = sessionStorage.getItem('user');
    const token = localStorage.getItem('authToken');

    // Only consider the user authenticated if both user data and token exist
    if (savedUser && token) {
      setUser(JSON.parse(savedUser));
    } else if (savedUser && !token) {
      // If user data exists but token doesn't, clear the session
      sessionStorage.removeItem('user');
    }

    setIsLoading(false);
  }, []);

  const getToken = () => {
    return localStorage.getItem('authToken');
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);

    try {
      const loginUrl = getApiUrl();
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        // Try to get error message from response
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Login failed');
        } catch (e) {
          // If parsing JSON fails, use status text
          throw new Error(`Login failed: ${response.statusText}`);
        }
      }

      const responseData = await response.json();

      // Store token in localStorage if it exists in the response
      if (Array.isArray(responseData) && responseData[0]?.token) {
        localStorage.setItem('authToken', responseData[0].token);
      }

      const userData = Array.isArray(responseData) ? responseData[0] : responseData;

      const user: User = {
        id: userData.id || '1',
        name: userData.name || 'User',
        email: userData.email || email,
        completedOnboarding: userData.completedOnboarding ?? true
      };

      setUser(user);
      sessionStorage.setItem('user', JSON.stringify(user));
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (name: string, email: string, password: string) => {
    setIsLoading(true);

    try {
      // Simulate API delay
      await new Promise(r => setTimeout(r, 1000));

      // Mock successful registration
      const mockUser: User = {
        id: '1',
        name,
        email,
        completedOnboarding: false // new users always need onboarding
      };

      // For signup, we should ideally get a token from the backend
      // For now, we'll just set a mock token
      localStorage.setItem('authToken', 'mock-token-for-new-signup');

      setUser(mockUser);
      sessionStorage.setItem('user', JSON.stringify(mockUser));
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    sessionStorage.removeItem('user');
    localStorage.removeItem('authToken');
  };

  const completeOnboarding = () => {
    if (user) {
      const updatedUser = { ...user, completedOnboarding: true };
      setUser(updatedUser);
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user && !!getToken(),
        isLoading,
        login,
        signup,
        logout,
        completeOnboarding,
        getToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
